import { AxiosError } from 'axios';
import { useParams } from 'react-router-dom';

import { PaginatedResponseV2 } from 'clipsal-cortex-types/src/common/types';
import { getStringifiedQueryParams } from 'clipsal-cortex-utils/src/formatting/query-params';

import {
  BatterySimulation,
  BatteryUpgradeOpportunity,
  BatteryUpgradeSummaryData,
  BatteryUpsellInvitationStatus,
  LoadProfile,
} from '../../api/api-battery-upsell';
import { ApiErrorDetail, get } from '../../api/api-helpers';
import { baseApi } from '../../app/baseApi';
import { useGetSiteUsersQuery } from '../site/customer-details/siteUsersApi';
import { BatteryUpgradeOpportunitiesQueryParams } from './battery-upsell-helpers';

export const batteryUpsellApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBatteryUpgradeOpportunities: builder.query<
      PaginatedResponseV2<BatteryUpgradeOpportunity>,
      BatteryUpgradeOpportunitiesQueryParams
    >({
      query: (params) => {
        const stringifiedParams = getStringifiedQueryParams<string>(params);
        return {
          url: `/fleet/battery_upgrade_opportunities?${stringifiedParams}`,
          method: 'GET',
        };
      },
      providesTags: (_, __, params) => [{ type: 'BatteryUpgradeOpportunities', ...params }],
    }),
    getBatterySimulations: builder.query<BatterySimulation[], number>({
      query: (siteId) => {
        return {
          url: `/fleet/sites/${siteId}/battery_simulations`,
          method: 'GET',
        };
      },
      providesTags: (_, __, siteId) => [{ type: 'BatterySimulations', siteId }],
    }),
    getLoadProfiles: builder.query<Record<string, LoadProfile[]>, number[]>({
      queryFn: async (simulationIds) => {
        try {
          const loadProfiles = await Promise.all(
            simulationIds.map((simulationId) => {
              return get<LoadProfile[]>(`/fleet/battery_simulations/${simulationId}/load_profile`);
            })
          );
          const data: Record<string, LoadProfile[]> = {};
          loadProfiles.forEach((loadProfile, index) => {
            const idString = simulationIds[index].toString();
            data[idString] = loadProfile;
          });

          return { data };
        } catch (e) {
          const error = e as AxiosError<ApiErrorDetail>;
          return {
            error: { message: error.response!.data.detail, status: error.response!.status },
          };
        }
      },
    }),
    getBatteryUpgradeSummary: builder.query<BatteryUpgradeSummaryData, void>({
      query: () => '/fleet/battery_upgrade_summary',
      providesTags: ['BatteryUpgradeSummary'],
    }),
    patchSiteInvitationStatus: builder.mutation<
      Pick<BatteryUpgradeOpportunity, 'clipsal_solar_id' | 'invitation_status'>,
      {
        invitationStatus: BatteryUpsellInvitationStatus;
        siteId: number;
      }
    >({
      query: ({ invitationStatus, siteId }) => {
        return {
          url: `/fleet/battery_upgrade_opportunities/${siteId}`,
          method: 'PATCH',
          body: {
            invitation_status: invitationStatus,
          },
        };
      },
      invalidatesTags: ['BatteryUpgradeSummary', 'BatteryUpgradeOpportunities'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetLoadProfilesQuery,
  useGetBatteryUpgradeOpportunitiesQuery,
  useGetBatterySimulationsQuery,
  useGetBatteryUpgradeSummaryQuery,
  usePatchSiteInvitationStatusMutation,
} = batteryUpsellApi;

export const useGetBatteryUpgradeOpportunities = (params: BatteryUpgradeOpportunitiesQueryParams, pageSize: number) => {
  const { data, isLoading, isFetching } = useGetBatteryUpgradeOpportunitiesQuery(params);
  const dataCount = data?.count || 0;
  const totalPages = Math.ceil(dataCount / pageSize);

  return { sites: data?.results || [], isLoading, isFetching, dataCount, totalPages };
};

export const useSiteUsers = (siteId: number) => {
  const { data, isLoading } = useGetSiteUsersQuery(siteId, { skip: !siteId });
  return { users: data || [], isLoading };
};

export const useBatterySimulations = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useGetBatterySimulationsQuery(Number(id));

  return { simulations: data || [], isLoading };
};

export const useTotalBatteryUpsellSites = () => {
  const { data, isLoading, isFetching } = useGetBatteryUpgradeSummaryQuery();

  const totalInterestedSites = data?.sites_invitation_status_interested || 0;
  const totalNotInterestedSites = data?.sites_invitation_status_not_interested || 0;
  const totalNotSetSites = data?.sites_invitation_status_not_set || 0;

  return {
    isFetchingTotalSites: isLoading || isFetching,
    total: {
      all: totalInterestedSites + totalNotInterestedSites + totalNotSetSites,
      interested: totalInterestedSites,
      not_interested: totalNotInterestedSites,
      not_set: totalNotSetSites,
    } as Record<BatteryUpsellInvitationStatus | 'all', number>,
  };
};

export const useLoadProfiles = (simulationIds: number[]) => {
  const { data, isLoading } = useGetLoadProfilesQuery(simulationIds, { skip: !simulationIds.length });

  return { loadProfiles: data || {}, isLoading };
};
